import { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router';
import { useAtomValue } from 'jotai';
import {
    isLoggedIn,
    openSsoLoginPopup,
    getAccessToken,
    decodeJwtPayload
} from 'common/utils/auth';
import { userStore } from 'store/userStore';
import { getUserInfo, UserInfo } from 'common/utils/secureStorage';

/**
 * 인증 상태를 관리하는 훅
 */
export const useAuth = () => {
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [userInfoLoaded, setUserInfoLoaded] = useState<boolean>(false);
    const location = useLocation();

    // SSO 회원정보 조회 상태
    const ssoUserState = useAtomValue(userStore.ssoUser.stateAtom);

    useEffect(() => {
        // 기존 토큰 확인
        const loggedIn = isLoggedIn();
        setIsAuthenticated(loggedIn);

        // 네이티브 웹뷰면 boot 시 한 번 더 토큰 sync.
        // 첫 로그인 시 SEND_TOKEN 이 iOS WKWebView 의 message bridge race(handler 등록 전 발사)
        // 로 손실되어 native MMKV 의 accessToken 이 비어버리는 회귀가 있어,
        // page entry 마다 localStorage 의 토큰을 다시 한 번 push.
        try {
            if (loggedIn && typeof window !== 'undefined' && (window as any).ReactNativeWebView) {
                const access = localStorage.getItem('accessToken');
                const refresh = localStorage.getItem('refreshToken');
                if (access) {
                    (window as any).ReactNativeWebView.postMessage(JSON.stringify({
                        type: 'SEND_TOKEN',
                        data: {
                            accessToken: access,
                            refreshToken: refresh || '',
                            access_token: access,
                            refresh_token: refresh || '',
                        },
                    }));
                }
            }
        } catch {
            /* postMessage 실패해도 web 흐름은 계속 */
        }

        // 로그인 상태이고 저장된 사용자 정보가 없으면 조회
        if (loggedIn && !getUserInfo()) {
            userStore.onGetSsoUser();
        } else {
            setUserInfoLoaded(true);
        }

        setIsLoading(false);
    }, []);

    // SSO 회원정보 조회 결과 처리
    useEffect(() => {
        if (ssoUserState.status === 'ready' && ssoUserState.data) {
            // 회원정보 암호화 저장
            userStore.onSaveUserInfo(ssoUserState.data);
            setUserInfoLoaded(true);
            userStore.ssoUser.onDefault();
        } else if (ssoUserState.status === 'error') {
            console.error('Failed to fetch user info:', ssoUserState.error);
            setUserInfoLoaded(true);
            userStore.ssoUser.onDefault();
        }
    }, [ssoUserState.status, ssoUserState.data]);

    const login = useCallback(() => {
        openSsoLoginPopup((data) => {
            // 팝업에서 토큰 수신 시 인증 상태 갱신
            setIsAuthenticated(true);
            userStore.onGetSsoUser();
        });
    }, []);

    const logout = () => {
        userStore.onLogout();
        setIsAuthenticated(false);
        setUserInfoLoaded(false);
    };

    return {
        isAuthenticated,
        isLoading,
        userInfoLoaded,
        login,
        logout,
        accessToken: getAccessToken()
    };
};

/**
 * 인증이 필요한 페이지에서 사용하는 훅
 * 로그인되어 있지 않으면 자동으로 SSO 로그인 페이지로 리다이렉트
 */
export const useRequireAuth = () => {
    const { isAuthenticated, isLoading, userInfoLoaded, login } = useAuth();

    useEffect(() => {
        if (!isLoading && !isAuthenticated) {
            login();
        }
    }, [isAuthenticated, isLoading]);

    return { isAuthenticated, isLoading, userInfoLoaded };
};

/**
 * JWT access token 에서 DOKOCREW 서비스 userNo / nickName 추출.
 * SSO /me 응답이 다른 service_id 의 user_no 를 리턴할 수 있어(크로스서비스 계정 충돌),
 * 현재 접속한 service 의 정확한 user_no 는 토큰 payload 가 권위적인 소스다.
 */
const getJwtIdentity = (): { userNo: number; nickname: string; serviceId: string } | null => {
    try {
        const token = getAccessToken();
        if (!token) return null;
        const payload = decodeJwtPayload(token);
        if (!payload?.userNo) return null;
        return {
            userNo: Number(payload.userNo),
            nickname: payload.nickName || '',
            serviceId: payload.serviceId || '',
        };
    } catch {
        return null;
    }
};

/**
 * 사용자 정보를 조회하는 훅
 * - userNo/serviceId: JWT 우선 (service 정합성 보장)
 * - nickname: SSO /me 저장값 우선 (JWT 는 발급 시점 스냅샷이라 프로필 수정이 즉시 반영 안 됨)
 */
export const useUserInfo = () => {
    const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
    const { isAuthenticated, userInfoLoaded } = useAuth();

    useEffect(() => {
        if (isAuthenticated && userInfoLoaded) {
            const info = getUserInfo();
            const jwt = getJwtIdentity();
            if (info && jwt) {
                setUserInfo({
                    ...info,
                    userNo: jwt.userNo,
                    // SSO 저장값(최신 프로필)이 있으면 우선, 없으면 JWT 스냅샷 fallback
                    nickname: info.nickname || jwt.nickname || '',
                });
            } else {
                setUserInfo(info);
            }
        } else {
            setUserInfo(null);
        }
    }, [isAuthenticated, userInfoLoaded]);

    return {
        userInfo,
        nickname: userInfo?.nickname || '',
        userId: userInfo?.userId || '',
        userNo: userInfo?.userNo || 0,
        ctn: userInfo?.ctn || '',
        age: userInfo?.age || '',
        profileImgPath: userInfo?.profileImgPath || '',
        profileImgNo: userInfo?.profileImgNo || '',
        statusMessage: userInfo?.statusMessage || '',
        isMinor: userInfo?.isMinor || 'N',
        locale: userInfo?.locale || 'ko',
        isLoaded: userInfoLoaded
    };
};
